import {
  Account,
  Criteria,
  Id,
  PaginatedAPIResponse,
  SalesOrder,
  SelectionFilter,
  SortDirection,
} from '@zspace/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AccountInboxItem from '../../shared/account-inbox-item/account-inbox-item';
import { useAdminContext } from '../../shared/context/admin-context';
import useHttpRequest from '../../shared/hooks/http-request';
import SalesOrderInboxItem from '../../shared/sales-order-inbox-item/sales-order-inbox-item';
import DefaultInboxTable from '../../ui/inbox-table/default-inbox-table/default-inbox-table';
import InboxItem from '../../ui/inbox-table/inbox-item/inbox-item';
import InboxTable from '../../ui/inbox-table/inbox-table';
import { Column, OnTableChangeData } from '../../ui/table/types';
import AdminInbox from '../admin-inbox/admin-inbox';
import {
  fetchDevicesBySalesOrder,
  fetchLicenseKeysBySalesOrder,
  fetchSalesOrdersByAccount,
} from '../admin-service';
import AdminTable, { AdminTableProps } from '../admin-table/admin-table';

export enum AdminToolsView {
  DEVICES = 'DEVICES',
  SALES_ORDERS = 'SALES_ORDERS',
  LICENSE_KEYS = 'LICENSE_KEYS',
}

export type AdminInboxTableProps<T extends Id, U extends Id, K = unknown> = {
  fetchMoreInboxItems: () => void;
  hasMoreInboxItems: boolean;
  inboxItems: U[];
  loadingInitialInboxItems: boolean;
  tableColumns: Column<T, K>[];
  searchValue: string;
  expandableRow?: AdminTableProps<T, K>['expandableRow'];
  initialCriteriaConfig?: Pick<Criteria, 'sortBy' | 'sortDirection'>;
};

const defaultInitialCriteria: Omit<Criteria, 'search'> = {
  itemsPerPage: 10,
  pageNumber: 1,
  sortBy: '',
  sortDirection: SortDirection.ASC,
};

export function AdminInboxTable<T extends Id, U extends Id, K = unknown>({
  fetchMoreInboxItems,
  hasMoreInboxItems,
  inboxItems,
  loadingInitialInboxItems,
  tableColumns,
  searchValue,
  expandableRow,
  initialCriteriaConfig,
}: AdminInboxTableProps<T, U, K>) {
  const {
    selectedInboxItem,
    setSelectedInboxItem,
    adminView,
    fetchTableRecordsErrorMessage,
    defaultInboxTableText,
    tableEmptyText,
    inboxEmptyText,
  } = useAdminContext();
  const latestDataFetchRef = useRef(0);
  const initialCriteria = useMemo(
    () => ({
      ...defaultInitialCriteria,
      ...initialCriteriaConfig,
    }),
    [initialCriteriaConfig]
  );
  const [criteria, setCriteria] = useState<Criteria>({
    ...initialCriteria,
    search: searchValue,
  });

  const [tableRecords, setTableRecords] = useState<T[]>([]);
  const { executeHttpRequest } = useHttpRequest();
  const [tableRecordsDataResponse, setTableRecordsDataResponse] =
    useState<PaginatedAPIResponse<T>>();
  const [loadingInitialTableRecords, setLoadingInitialTableRecords] =
    useState<boolean>(false);

  const tableConfig = useMemo(() => {
    const { sortBy, sortDirection } = criteria;
    return {
      search: searchValue,
      sortBy,
      sortDirection,
    };
  }, [criteria, searchValue]);

  const fetchTableRecords = useCallback(
    async (inboxItemId: string, criteria: Criteria, clearData?: boolean) => {
      const currentRef = ++latestDataFetchRef.current;

      return executeHttpRequest({
        asyncFunction: async () => {
          let fetchTableRecordsFn: (
            arg0: string,
            arg1: Criteria
          ) => Promise<PaginatedAPIResponse<T>>;

          switch (adminView) {
            case AdminToolsView.DEVICES:
              fetchTableRecordsFn =
                fetchDevicesBySalesOrder as unknown as typeof fetchTableRecordsFn;
              break;
            case AdminToolsView.SALES_ORDERS:
              fetchTableRecordsFn =
                fetchSalesOrdersByAccount as unknown as typeof fetchTableRecordsFn;
              break;
            case AdminToolsView.LICENSE_KEYS:
              fetchTableRecordsFn =
                fetchLicenseKeysBySalesOrder as unknown as typeof fetchTableRecordsFn;
              break;
            default:
              return;
          }

          const response = await fetchTableRecordsFn!(inboxItemId, criteria);
          if (currentRef !== latestDataFetchRef.current) return;

          if (clearData) {
            setTableRecords(response.data);
          } else {
            setTableRecords((prevTableRecords) =>
              prevTableRecords.concat(response.data)
            );
          }
          setTableRecordsDataResponse(response);
        },
        customErrorMessage: fetchTableRecordsErrorMessage,
      });
    },
    [adminView, executeHttpRequest, fetchTableRecordsErrorMessage]
  );

  const onSelectInboxItem = useCallback(
    async (inboxItemId: string) => {
      const { itemsPerPage, pageNumber, sortBy, sortDirection } =
        initialCriteria;
      const updatedCriteria = {
        ...criteria,
        itemsPerPage,
        pageNumber,
        sortBy,
        sortDirection,
      };
      setCriteria(updatedCriteria);

      setSelectedInboxItem(inboxItemId);
      setLoadingInitialTableRecords(true);
      await fetchTableRecords(inboxItemId, updatedCriteria, true);
      setLoadingInitialTableRecords(false);
    },
    [criteria, fetchTableRecords, initialCriteria, setSelectedInboxItem]
  );

  const fetchMoreTableRecords = useCallback(async () => {
    const updatedCriteriaFilter = {
      ...criteria,
      pageNumber: criteria.pageNumber + 1,
    };
    if (selectedInboxItem) {
      await fetchTableRecords(selectedInboxItem, updatedCriteriaFilter);
      setCriteria(updatedCriteriaFilter);
    }
  }, [criteria, fetchTableRecords, selectedInboxItem]);

  const onTableChange = useCallback(
    async (value: OnTableChangeData<T>) => {
      const updatedCriteria = {
        ...value.config,
        sortBy: value.column?.key || criteria.sortBy,
      };
      const { pageNumber } = initialCriteria;
      setCriteria((prevCriteria) => ({
        ...prevCriteria,
        ...updatedCriteria,
        pageNumber,
      }));

      setLoadingInitialTableRecords(true);
      await fetchTableRecords(
        selectedInboxItem!,
        {
          ...criteria,
          ...updatedCriteria,
        },
        true
      );
      setLoadingInitialTableRecords(false);
    },
    [criteria, fetchTableRecords, initialCriteria, selectedInboxItem]
  );

  const hasMoreTableRecords = useMemo(
    () => !!tableRecordsDataResponse?.hasMore,
    [tableRecordsDataResponse]
  );

  const table = useMemo(() => {
    if (selectedInboxItem) {
      return (
        <AdminTable
          dataSource={tableRecords}
          columns={tableColumns}
          config={tableConfig}
          fetchMore={fetchMoreTableRecords}
          hasMore={hasMoreTableRecords}
          loadingInitialData={loadingInitialTableRecords}
          onChange={onTableChange}
          emptyText={tableEmptyText}
          expandableRow={expandableRow}
        />
      );
    }
    return <DefaultInboxTable text={defaultInboxTableText} />;
  }, [
    defaultInboxTableText,
    expandableRow,
    fetchMoreTableRecords,
    hasMoreTableRecords,
    loadingInitialTableRecords,
    onTableChange,
    selectedInboxItem,
    tableColumns,
    tableConfig,
    tableEmptyText,
    tableRecords,
  ]);

  const inboxHeader = useMemo(() => {
    let itemsLabel = '';
    switch (adminView) {
      case AdminToolsView.DEVICES: {
        itemsLabel = 'devices';
        break;
      }
      case AdminToolsView.LICENSE_KEYS: {
        itemsLabel = 'license keys';
        break;
      }
      case AdminToolsView.SALES_ORDERS: {
        itemsLabel = 'sales orders';
        break;
      }
      default:
        return null;
    }
    return (
      <InboxItem
        active={selectedInboxItem === SelectionFilter.ALL}
        onClick={() => onSelectInboxItem(SelectionFilter.ALL)}
      >
        All {itemsLabel}
      </InboxItem>
    );
  }, [adminView, onSelectInboxItem, selectedInboxItem]);

  const inboxItem = useCallback(
    (item: U) => {
      switch (adminView) {
        case AdminToolsView.DEVICES:
        case AdminToolsView.LICENSE_KEYS:
          return (
            <SalesOrderInboxItem
              active={selectedInboxItem === item.id}
              salesOrder={item as unknown as SalesOrder}
              onClick={() => onSelectInboxItem(item.id)}
            />
          );
        case AdminToolsView.SALES_ORDERS:
          return (
            <AccountInboxItem
              active={selectedInboxItem === item.id}
              account={item as unknown as Account}
              onClick={() => onSelectInboxItem(item.id)}
            />
          );
        default:
          return null;
      }
    },
    [adminView, onSelectInboxItem, selectedInboxItem]
  );

  const inbox = useMemo(
    () => (
      <AdminInbox
        fetchMore={fetchMoreInboxItems}
        hasMore={hasMoreInboxItems}
        inboxItems={inboxItems}
        loadingInitialData={loadingInitialInboxItems}
        header={inboxHeader}
        emptyText={inboxEmptyText}
        renderInboxItem={(item: U) => inboxItem(item)}
      />
    ),
    [
      fetchMoreInboxItems,
      hasMoreInboxItems,
      inboxEmptyText,
      inboxHeader,
      inboxItem,
      inboxItems,
      loadingInitialInboxItems,
    ]
  );

  useEffect(() => {
    setSelectedInboxItem(undefined);
  }, [searchValue, setSelectedInboxItem]);

  useEffect(() => {
    setCriteria((prevCriteria) => ({ ...prevCriteria, search: searchValue }));
  }, [searchValue]);

  return <InboxTable inbox={inbox} table={table} enableSearch={false} />;
}

export default AdminInboxTable;
